"use client";

import React from "react";
import { UserCheck, Zap, PiggyBank, ShieldCheck, CircleCheck as CheckCircle2, CircleX as XCircle, ArrowRight, Sparkles, ChartBar as BarChart3, MessageSquare } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const benefitsList = [{
  id: "1",
  title: "100% Direct Consultant Access",
  subtitle: "No Junior Account Managers",
  description: "When you hire YE Consultancy, you work directly with me, Yassine Essaadi. Agencies routinely pitch with senior directors but pass daily execution to inexperienced account managers.",
  impact: "Direct access to senior expertise on every call and strategy document.",
  iconName: "UserCheck"
}, {
  id: "2",
  title: "Agile & Fast Execution",
  subtitle: "Zero Bureaucratic Delays",
  description: "Without multi-layered approval chains, corporate red tape, or internal agency queue systems, I implement technical updates and strategy adjustments in days, not months.",
  impact: "Faster implementation leads directly to quicker organic traffic recovery and growth.",
  iconName: "Zap"
}, {
  id: "3",
  title: "Maximum Cost Efficiency",
  subtitle: "Pay for Senior Strategy, Not Agency Overhead",
  description: "Agency retainers cover fancy city-center offices, sales teams, and management layers. My pricing is lean and fully aligned with direct strategic value and high-impact deliverables.",
  impact: "100% of your budget goes directly toward expert execution and measurable results.",
  iconName: "PiggyBank"
}, {
  id: "4",
  title: "Bespoke SEO Strategy",
  subtitle: "No Recycled Checklist Templates",
  description: "Agencies rely on automated tool exports and rigid monthly deliverable templates. I build custom SEO roadmaps based on your specific technical architecture and commercial goals.",
  impact: "Tailored strategies designed for sustainable search dominance and lead generation.",
  iconName: "Sparkles"
}, {
  id: "5",
  title: "Full Accountability & Ownership",
  subtitle: "Single Point of Expertise",
  description: "There is no blame shifting between departments. I take personal responsibility for your site's organic performance, ranking health, and technical compliance.",
  impact: "Clear deliverables, proactive communications, and total dedication to your success.",
  iconName: "ShieldCheck"
}, {
  id: "6",
  title: "Transparent & Honest Reporting",
  subtitle: "Focus on Revenue, Not Vanity Metrics",
  description: "I focus on actual business metrics like high-intent leads, sales conversions, and qualified organic revenue rather than inflated keyword reports and vanity numbers.",
  impact: "Honest measurement and pragmatic advice on what genuinely moves the needle.",
  iconName: "BarChart3"
}];
const comparisonMatrix = [{
  feature: "Primary Contact",
  consultant: "Yassine Essaadi (Senior SEO Lead)",
  agency: "Junior Account Manager"
}, {
  feature: "Strategy Origin",
  consultant: "Bespoke roadmap based on deep audits",
  agency: "Standardized agency template"
}, {
  feature: "Turnaround Speed",
  consultant: "Direct updates, agile & rapid turnaround",
  agency: "Multi-week internal review queues"
}, {
  feature: "Overhead Cost",
  consultant: "Zero fluff, 100% focused on performance",
  agency: "High margin for sales team & office space"
}, {
  feature: "Communication",
  consultant: "Direct line via Slack, email & weekly syncs",
  agency: "Scheduled monthly account manager updates"
}];
export default function Consultantbenefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="bg-background text-foreground py-16 md:py-24 transition-colors">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-sm font-medium bg-secondary text-secondary-foreground border border-accent/20">
            <Sparkles className="size-3.5 mr-1 text-accent" />
            Independent SEO Expertise
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
            Why Work Directly With Me Instead of an SEO Agency?
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-default">
            I built my freelance SEO consultancy around one core promise: <strong className="text-foreground">you work directly with me</strong>. No sales reps, no junior account managers, and no generic agency templates. Just dedicated, senior-level SEO strategy tailored to your business growth.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-20">
          {benefitsList.map((item, index) => {
          return <motion.div key={item.id} variants={itemVariants} data-index={index}>
                <Card className="h-full border border-border bg-card hover:border-accent/50 transition-[border-color,box-shadow] hover:shadow-md relative overflow-hidden group">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-accent/30 group-hover:bg-accent transition-colors" />

                  <CardHeader className="pt-6 pb-2">
                    <div className="size-12 rounded-xl bg-secondary flex items-center justify-center text-primary mb-4 border border-accent/20">
                      {item.iconName === "UserCheck" && <UserCheck className="size-6 text-primary" />}
                      {item.iconName === "Zap" && <Zap className="size-6 text-primary" />}
                      {item.iconName === "PiggyBank" && <PiggyBank className="size-6 text-primary" />}
                      {item.iconName === "Sparkles" && <Sparkles className="size-6 text-primary" />}
                      {item.iconName === "ShieldCheck" && <ShieldCheck className="size-6 text-primary" />}
                      {item.iconName === "BarChart3" && <BarChart3 className="size-6 text-primary" />}
                    </div>
                    <Badge variant="outline" className="w-fit text-xs font-normal border-accent/30 text-accent-foreground bg-accent/10 mb-1">
                      {item.subtitle}
                    </Badge>
                    <CardTitle className="text-xl font-semibold text-foreground font-default">
                      {item.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-2">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed font-default">
                      {item.description}
                    </CardDescription>

                    <Separator className="bg-border/60" />

                    <div className="flex items-start gap-2 text-xs font-medium text-foreground bg-muted/60 p-3 rounded-lg border border-border/40">
                      <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                      <span>{item.impact}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="max-w-5xl mx-auto bg-card rounded-2xl border border-border p-6 sm:p-8 lg:p-10 shadow-sm mb-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <Badge variant="outline" className="mb-2 border-primary/20 text-primary">
              Direct Comparison
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-semibold text-foreground font-default">
              Independent SEO Consultant vs. Traditional SEO Agency
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base mt-2 font-default">
              Compare the operational structure and strategic value before committing your search budget.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[600px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-4 px-4 font-semibold text-foreground text-sm w-1/3">Key Criteria</th>
                  <th className="py-4 px-4 font-semibold text-primary text-sm bg-secondary/50 rounded-t-lg w-1/3">
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="size-4 text-primary" />
                      <span>YE Consultancy (Yassine)</span>
                    </div>
                  </th>
                  <th className="py-4 px-4 font-semibold text-muted-foreground text-sm w-1/3">
                    <div className="flex items-center gap-1.5">
                      <XCircle className="size-4 text-destructive/70" />
                      <span>Traditional Agency</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {comparisonMatrix.map((row, idx) => <tr key={row.feature} data-index={idx} className="hover:bg-muted/30 transition-colors">
                    <td className="py-4 px-4 font-medium text-foreground text-sm">
                      {row.feature}
                    </td>
                    <td className="py-4 px-4 text-sm font-medium text-foreground bg-secondary/20">
                      {row.consultant}
                    </td>
                    <td className="py-4 px-4 text-sm text-muted-foreground">
                      {row.agency}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="max-w-4xl mx-auto bg-secondary/40 border border-accent/20 rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row items-center gap-6 md:gap-8">
          <div className="shrink-0 relative">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant" loading="lazy" decoding="async" className="size-24 sm:size-28 rounded-full object-cover border-2 border-accent shadow-sm" />
            <div className="absolute -bottom-1 -right-1 bg-primary text-primary-foreground p-1.5 rounded-full shadow-xs">
              <MessageSquare className="size-4" />
            </div>
          </div>

          <div className="space-y-3 text-center md:text-left">
            <p className="text-foreground italic text-base sm:text-lg leading-relaxed font-default">
              "My mission is simple: provide UK and international businesses with clear, high-impact SEO strategy without agency fluff or overheads. When you hire me, I personally ensure every technical fix and content strategy drives organic revenue."
            </p>
            <div>
              <h4 className="font-semibold text-foreground text-base font-default">Yassine Essaadi</h4>
              <p className="text-xs text-muted-foreground font-default">Independent SEO Strategy & Technical Consultant</p>
            </div>
          </div>
        </div>

        <div className="mt-16 text-center space-y-6 max-w-2xl mx-auto">
          <h3 className="text-2xl font-semibold text-foreground font-default">
            Ready for direct expert guidance on your search strategy?
          </h3>
          <p className="text-muted-foreground text-sm sm:text-base">
            Book a complimentary 30-minute consultation directly with me to discuss your organic growth targets.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <Button asChild size="lg" className="w-full sm:w-auto px-8 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto px-6 border-border hover:bg-accent/10">
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}